// Copyright 2025, University of Maryland, All Rights Reserved
import { ColumnDef } from "@tanstack/react-table";
import { WorkspaceBill, WorkspaceBillAction } from "@/types/workspace-bill";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { FileText, ArrowUp, ArrowDown } from "lucide-react";
import { BillActionDropdown } from "@/components/BillActionDropdown";
import { BillScoreCell } from "@/components/workspace-bills/BillScoreCell";
import { getFilterScore } from "@/utils/monitor-analysis";
import type { Config } from "@/api/types/config";
import { AILegislativeReview } from "@/components/workspace-bills/AILegislativeReview.tsx";
import { SortField, SortDirection } from '@/hooks/workspace-bills/useWorkspaceBillsWorkflow';

function formatToEastern(dateString?: string) {
  // Example fallback
  return dateString ? new Date(dateString).toLocaleString("en-US", { timeZone: "America/New_York" }) : "";
}

interface WorkspaceBillsColumnsProps {
  processing_actions?: Record<string, WorkspaceBillAction>;
  selected_bills: string[];
  selected_session: string;
  on_select_bill: (bill_id: string) => void;
  on_select_all: (bills: WorkspaceBill[]) => void;
  on_process_action: (bill_id: string, action: WorkspaceBillAction) => void;
  on_show_impact_details?: (bill: WorkspaceBill) => void;
  handle_pdf_click: (url: string, e: React.MouseEvent) => void;
  sort_field?: SortField;
  sort_direction?: SortDirection;
  on_sort_change?: (field: SortField, direction: SortDirection) => void;
  config?: Config | null;
}

export const getWorkspaceBillsColumns = (props: WorkspaceBillsColumnsProps): ColumnDef<WorkspaceBill>[] => {
  const {
    selected_bills,
    on_select_bill,
    on_select_all,
    on_process_action,
    on_show_impact_details,
    handle_pdf_click,
    processing_actions,
    sort_field,
    sort_direction,
    on_sort_change,
  } = props;

  return [
    {
      id: 'select',
      header: ({ table }) => (
          <div className="flex items-center">
      <span className="sr-only">Select</span>
        <Checkbox
          checked={selected_bills.length === table.getFilteredSelectedRowModel().rows.length && selected_bills.length > 0}
          onCheckedChange={() => on_select_all(table.getFilteredRowModel().rows.map(row => row.original))}
          aria-label="Select all"
        />
            </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selected_bills.includes(row.original.bill_number)}
          onCheckedChange={() => on_select_bill(row.original.bill_number)}
          aria-label={`Select bill ${row.original.bill_number}`}
        />
      ),
      enableSorting: false,
      size: 12,
    },
    {
      accessorKey: 'bill_number',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => on_sort_change?.('bill_number', sort_field === 'bill_number' && sort_direction === 'desc' ? 'asc' : 'desc')}
          >
            <span>Bill Number</span>
            {sort_field === 'bill_number' && (
              sort_direction === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => {
        const bill_number = row.original.bill_number;
        
        return (
          <div className="flex items-center gap-2">
            {row.original.bill_number}
            {row.original.pdf_url && row.original.full_text_available && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handle_pdf_click(row.original.pdf_url, e)}
                      className="px-2 w-8 h-8"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="sr-only">View Bill PDF</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    View Bill PDF
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {row.original.versions && row.original.versions.length > 0 && (
              <AILegislativeReview
                latestVersion={row.original.versions[row.original.versions.length - 1]}
                bill={row.original}
                loadingAction={processing_actions?.[bill_number]}
              />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'title',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => on_sort_change?.('title', sort_field === 'title' && sort_direction === 'desc' ? 'asc' : 'desc')}
          >
            <span>Title</span>
            {sort_field === 'title' && (
              sort_direction === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className="truncate max-w-md">
                {row.original.title || 'No Title'}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-lg">
              {row.original.synopsis || 'No synopsis available'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      id: 'impact',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => on_sort_change?.('impact', sort_field === 'impact' && sort_direction === 'desc' ? 'asc' : 'desc')}
          >
            <span>Impact</span>
            {sort_field === 'impact' && (
              sort_direction === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      // Create a custom accessor function that generates a sortable value for impact
      accessorFn: (bill: WorkspaceBill) => {
        const impact = getFilterScore(bill.monitor_analysis);
        if (!impact) return { type: 'no-analysis', value: -999 }; // No analysis should sort to the end

        // Create a numeric value for sorting that prioritizes type first, then score
        // This creates three tiers (positive, neutral, negative) with scores affecting order within each tier
        const typeValue: Record<string, number> = { positive: 1000, neutral: 0, negative: -1000 };
        return {
          type: impact.type,
          score: impact.score,
          value: typeValue[impact.type] + impact.score
        };
      },
      cell: ({ row }) => (
        <BillScoreCell
          analysis={row.original.monitor_analysis}
          onClick={() => on_show_impact_details?.(row.original)}
        />
      ),
    },
    {
      accessorKey: 'crossfile_bill_number',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => on_sort_change?.('crossfile_bill_number', sort_field === 'crossfile_bill_number' && sort_direction === 'desc' ? 'asc' : 'desc')}
          >
            <span>Crossfile</span>
            {sort_field === 'crossfile_bill_number' && (
              sort_direction === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => (
        row.original.crossfile_bill_number ? (
          <Badge variant="outline">
            {row.original.crossfile_bill_number}
          </Badge>
        ) : (
          "-"
        )
      ),
    },
    {
      accessorKey: 'last_modified',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => on_sort_change?.('last_modified', sort_field === 'last_modified' && sort_direction === 'desc' ? 'asc' : 'desc')}
          >
            <span>Modified</span>
            {sort_field === 'last_modified' && (
              sort_direction === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => formatToEastern(row.original.last_modified),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const bill = row.original;
        return (
          <div className="flex justify-end gap-2">
            <BillActionDropdown
              onAction={(action: WorkspaceBillAction) => on_process_action(bill.bill_number, action)}
              disabled={!bill.full_text_available || !!processing_actions?.[bill.bill_number]}
            />
          </div>
        );
      },
      enableSorting: false,
      size: 100,
    },
  ];
};